/**
 * Breadcrumbs — small uppercase trail rendered above hero on category and
 * detail pages. Emits BreadcrumbList JSON-LD alongside the visual line.
 *
 * items = [{ name, path }] — first crumb is usually Home.
 */

import { Link } from "react-router-dom";
import { breadcrumbSchema } from "@/lib/seo/schemas";
import { safeJsonLd } from "@/lib/seo/safeJson";

export const Breadcrumbs = ({ items = [], tone = "dark" }) => {
  if (!items.length) return null;

  const isDark = tone === "dark";
  const labelCls = isDark
    ? "text-white/50 hover:text-[#c6a87c]"
    : "text-[#0a0a0c]/50 hover:text-[#c6a87c]";
  const separatorCls = isDark ? "text-white/25" : "text-[#0a0a0c]/25";
  const currentCls = isDark ? "text-[#c6a87c]" : "text-[#c6a87c]";

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        data-testid="seo-breadcrumbs"
        className="flex flex-wrap items-center justify-center gap-2 font-tech text-[10px] uppercase tracking-[0.32em]"
      >
        {items.map((c, i) => {
          const isLast = i === items.length - 1;
          return (
            <span key={c.path + c.name} className="flex items-center gap-2">
              {isLast ? (
                <span className={currentCls} aria-current="page">
                  {c.name}
                </span>
              ) : (
                <Link
                  to={c.path}
                  className={`transition-colors duration-300 ${labelCls}`}
                >
                  {c.name}
                </Link>
              )}
              {!isLast && <span className={separatorCls}>/</span>}
            </span>
          );
        })}
      </nav>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: safeJsonLd(breadcrumbSchema(items)),
        }}
      />
    </>
  );
};

export default Breadcrumbs;
